<template>
  <div class="feed-body">
    <div class="feed-contents">
      <GmapMap
        :center="effectiveCenter"
        :zoom="effectiveZoom"
        :bounds="computedBounds"
        map-type-id="terrain"
        class="map"
        v-if="effectiveCenter"
        :options="
          $vuetify.breakpoint.xs
            ? {
                fullscreenControl: false
              }
            : null
        "
      >
        <GmapMarker
          :key="index"
          v-for="(m, index) in markers"
          :position="m.position"
          :clickable="true"
          :icon="m.icon"
          @click="onClickedMarker(m, index)"
        >
          <GmapInfoWindow
            :opened.sync="m.ifw"
            :options="{
              pixelOffset: { width: 0, height: 0 },
              content: m.template
            }"
            @closeclick="m.ifw = false"
          />
        </GmapMarker>
      </GmapMap>
      <v-btn
        style="position: absolute; top: 10px; right: 10px; padding: 0; min-width: 50px; height: 40px;"
        tile
        @click="openRightSection"
        v-if="$vuetify.breakpoint.xs"
      >
        <v-icon style="margin-top: 2.5px;">mdi-menu</v-icon>
      </v-btn>
      <v-btn
        style="position: absolute; top: 10px; left: 10px; padding: 0; min-width: 50px; height: 40px;"
        tile
        @click="requestAndSetUserLocation"
        v-if="userLocationDenied || !userLocation"
        color="primary"
        title="Enable location to see needs near you"
      >
        <v-icon style="margin-top: 2.5px;">mdi-crosshairs-gps</v-icon>
      </v-btn>
    </div>
    <v-dialog v-model="rightSectionDialog" v-if="rightSectionDialog">
      <v-card>
        <v-card-text class="pt-4 pb-8">
          <agency-need-card
            :need="need"
            v-for="need in needs"
            :key="need._id"
            :hideButtons="true"
            class="mt-4"
            :onClicked="onSelected"
          />
          <div v-if="!needs.length" class="mt-10 mx-10">
            No needs found.
            <div v-if="profileType == 'Agency'">
              Please create new needs.
            </div>
          </div>
        </v-card-text>
      </v-card>
    </v-dialog>
    <v-snackbar v-model="snackbar">
      {{ errorMessage }}
    </v-snackbar>
    <transition name="fade">
      <div
        v-if="showNoNeedsAlert"
        class="no-needs-alert"
        style="position: fixed; left: 0; right: 0; bottom: 0; z-index: 9999; background: #fffbe6; color: #ad6800; text-align: center; padding: 16px; font-weight: bold; border-top: 1px solid #ffe58f;"
      >
        No needs within 20-30 miles of your location. You can zoom out to see needs at other location.
      </div>
    </transition>
  </div>
</template>
<script>
import { mapActions, mapGetters } from "vuex";
import AgencyNeedCard from "../agency/needs/AgencyNeedCard.vue";
export default {
  data() {
    return {
      loading: false,
      errorMessage: null,
      snackbar: false,
      loadCompleted: -1,
      loadingMore: false,
      center: null,
      markers: [],
      rightSectionDialog: false,
      fallbackCenter: { lat: 42.2788668, lng: -71.4206997 }, // Add a fallback center
      userLocation: null,
      userLocationDenied: false,
      userLocationZoom: 11, // ~20 mile radius
      userLocationBounds: null, // bounds for user location
      showNoNeedsAlert: false,
      noNeedsAlertTimeout: null
    };
  },
  computed: {
    ...mapGetters({
      profileType: "auth/getType",
      profile: "auth/getProfile",
      needs: "need/getAllNeeds",
      unit: "usersetting/getUnit"
    }),
    computedBounds() {
      // If user location is available and enabled, set bounds to 20-30 mile radius around user
      if (this.userLocation && !this.userLocationDenied) {
        // Approx 1 degree latitude ~ 69 miles, so 20 miles ~ 0.29 deg
        const lat = this.userLocation.lat;
        const lng = this.userLocation.lng;
        const delta = 0.3; // ~20-21 miles
        return {
          north: lat + delta,
          south: lat - delta,
          east: lng + delta,
          west: lng - delta
        };
      }
      // If no markers or google is not defined, return null
      if (!this.markers.length || typeof google === "undefined") {
        return null;
      }
      const bounds = new google.maps.LatLngBounds();
      this.markers.forEach(marker => {
        bounds.extend(marker.position);
      });
      const ne = bounds.getNorthEast();
      const sw = bounds.getSouthWest();
      return {
        north: ne.lat(),
        east: ne.lng(),
        south: sw.lat(),
        west: sw.lng()
      };
    },
    computedCenter() {
      if (!this.markers.length || typeof google === "undefined") {
        return null;
      }
      const bounds = new google.maps.LatLngBounds();
      this.markers.forEach(marker => bounds.extend(marker.position));
      return {
        lat: bounds.getCenter().lat(),
        lng: bounds.getCenter().lng()
      };
    },
    computedZoom() {
      if (!this.markers.length || typeof google === "undefined") {
        return 13;
      }
      if (this.markers.length === 1) {
        return 13;
      }
      const bounds = new google.maps.LatLngBounds();
      this.markers.forEach(marker => bounds.extend(marker.position));
      const mapDiv = document.querySelector(".map");
      if (mapDiv) {
        const mapSize = {
          width: mapDiv.offsetWidth,
          height: mapDiv.offsetHeight
        };
        // Use calculated zoom directly, do not force minimum zoom
        if (bounds.isEmpty()) {
          return 13;
        }
        return this.calculateZoomLevel(bounds, mapSize);
      }
      return 13;
    },
    effectiveCenter() {
      // If user location is available and enabled, use it as center
      if (this.userLocation && !this.userLocationDenied) {
        return this.userLocation;
      }
      // If user denied location or location not available, use computedCenter from markers
      return this.computedCenter || this.fallbackCenter;
    },
    effectiveZoom() {
      // If user location is available and enabled, use zoom for 20-30 mile radius
      if (this.userLocation && !this.userLocationDenied) {
        return 11; // suitable for ~20 mile radius
      }
      // If user denied location or location not available, use computedZoom from markers
      if (this.userLocationDenied || !this.userLocation) {
        return this.computedZoom;
      }
      // If userLocation is available and allowed, use it (unless there are markers to show)
      if (this.userLocation && !this.markers.length) {
        return this.userLocationZoom;
      }
      return this.computedZoom;
    },
    needsWithinUserRadius() {
      // Only check if user location is available and enabled
      if (!(this.userLocation && !this.userLocationDenied)) return true;
      // 20 miles in degrees latitude/longitude (~0.29)
      const delta = 0.3;
      const lat = this.userLocation.lat;
      const lng = this.userLocation.lng;
      return this.markers.some(m =>
        Math.abs(m.position.lat - lat) <= delta &&
        Math.abs(m.position.lng - lng) <= delta
      );
    }
  },
  methods: {
    ...mapActions({
      fetchAllNeeds: "need/fetchAllNeeds",
      getLocation: "need/getLocation",
      getCurrentWeather: "weather/getCurrentWeather"
    }),
    showErrorMessage(message) {
      this.errorMessage = message;
      this.snackbar = true;
    },
    getLocationFromAddress() {
      var address = `${this.profile.address}${
        this.profile.address2 ? " " + this.profile.address2 : ""
      }, ${this.profile.city}, ${this.profile.state} ${this.profile.zip}`;
      this.getLocation(address).then(() => {
        // Removed unused variable `res`
      });
    },
    onClickedMarker(m, index) {
      this.markers = this.markers.map(m => {
        m.ifw = false;
        return m;
      });
      this.markers[index].ifw = true;
    },
    async loadMarkers() {
      this.markers = [];
      for (let i = 0; i < this.needs.length; i++) {
        try {
          var need = this.needs[i];
          var template = "<h2 style='color:#482684;'>" + need.title + "</h2>";
          var icon = { url: require("@/assets/marker_need.png") };
          if (need.agency_name)
            template +=
              "<br><h4 style='color:#8e5ee3;'>" + need.agency_name + "</h4>";
          if (need.duration)
            template +=
              "<br><h4 style='color:#8e5ee3;'>" + need.duration + "</h4>";
          template += `<br><a href='/needs/details?_id=${need._id}' target="_blank">View details</a>`;
          var lc = await this.getLocation(
            `${need.address ? need.address : ""}${
              need.address2 ? " " + need.address2 + "," : ""
            } ${need.city ? need.city + "," : ""} ${
              need.state ? need.state : ""
            } ${need.zip ? need.zip : ""}`.trim()
          );
          this.markers.push({
            position: lc,
            ifw: false,
            label: need.title,
            template: `<b>${template}</b>`,
            icon
          });
        } catch (error) {
          console.log(error);
        }
      }
      this.mergeMarkers();
      for (let i = 0; i < this.markers.length; i++) {
        try {
          const weather = await this.getCurrentWeather({
            unit: this.unit,
            ...this.markers[i].position
          });
          let weatherTemplate = `
          <div style="display: flex; flex-direction: row; align-items: center;background-color: #f5faff; border-radius: 10px; padding-right: 10px; padding-top: 5px; padding-bottom: 5px">
            <img src="${this.forecastIcon(
              weather.weather[0]["icon"]
            )}" style="width: 72px; height: 72px"/>
            <div style="display: flex; flex-direction: column; margin-left: 10px;">
              <div style="font-weight: bold; color: #0A5B8A; font-size: 16px;">${
                weather.main.temp
              }&deg;${this.unit == "imperial" ? "F" : "C"}</div>
              <div style="display: flex; flex-direction: row; margin-top: 5px; margin-bottom: 5px;">
                <span>${weather.main.temp_max}&deg;${
            this.unit == "imperial" ? "F" : "C"
          }</span>
                <span style="margin-left: 10px; color: #919191">${
                  weather.main.temp_min
                }&deg;${this.unit == "imperial" ? "F" : "C"}</span>
              </div>
              <div style="font-weight: bold; color: black">
                ${this.getFirstCap(weather.weather[0].description)}
                </div>
            </div>
          </div>`;
          this.markers[i].template += "<br><br><br>" + weatherTemplate;
        } catch (error) {
          console.log(error);
        }
      }
    },
    mergeMarkers() {
      var index = 0;
      while (index < this.markers.length) {
        var ok = true;
        for (var i = index + 1; i < this.markers.length; i++) {
          if (
            Math.abs(
              this.markers[i].position.lat - this.markers[index].position.lat
            ) < 0.000001 &&
            Math.abs(
              this.markers[i].position.lng - this.markers[index].position.lng
            ) < 0.000001
          ) {
            this.markers[index].template +=
              "<br><br><br>" + this.markers[i].template;
            ok = false;
            this.markers.splice(i, 1);
            break;
          }
        }
        if (ok) index++;
      }
    },
    forecastIcon(param) {
      let icon = "";
      try {
        icon = param || "01d";
      } catch {
        icon = "01d";
      }
      return require(`@/assets/weather/${icon}.png`);
    },
    getFirstCap(res) {
      let desc = res ? res : "";
      return desc.charAt(0).toUpperCase() + desc.slice(1);
    },
    openRightSection() {
      this.rightSectionDialog = true;
    },
    onSelected(need) {
      if (this.$route.query._id != need) {
        this.$router.replace({ query: { _id: need } });
      }
      this.rightSectionDialog = false;
    },
    calculateZoomLevel(bounds, mapSize) {
      const WORLD_DIM = { width: 256, height: 256 };
      const ZOOM_MAX = 21;

      function latRad(lat) {
        const sin = Math.sin((lat * Math.PI) / 180);
        const radX2 = Math.log((1 + sin) / (1 - sin)) / 2;
        return Math.max(Math.min(radX2, Math.PI), -Math.PI) / 2;
      }

      function zoom(mapPx, worldPx, fraction) {
        return Math.floor(Math.log(mapPx / worldPx / fraction) / Math.LN2);
      }

      const ne = bounds.getNorthEast();
      const sw = bounds.getSouthWest();

      const latFraction = (latRad(ne.lat()) - latRad(sw.lat())) / Math.PI;
      const lngDiff = ne.lng() - sw.lng();
      const lngFraction = (lngDiff < 0 ? lngDiff + 360 : lngDiff) / 360;

      const latZoom = zoom(mapSize.height, WORLD_DIM.height, latFraction);
      const lngZoom = zoom(mapSize.width, WORLD_DIM.width, lngFraction);

      return Math.min(latZoom, lngZoom, ZOOM_MAX);
    },
    requestAndSetUserLocation() {
      this.userLocation = null;
      this.userLocationDenied = false;
      
      // Check if geolocation is supported
      if (!navigator.geolocation) {
        console.warn("Geolocation is not supported by this browser");
        this.userLocationDenied = true;
        this.showErrorMessage("Geolocation is not supported by this browser");
        return;
      }

      // Check if we're in a secure context (HTTPS or localhost)
      if (
        location.protocol !== "https:" &&
        location.hostname !== "localhost" &&
        location.hostname !== "127.0.0.1"
      ) {
        console.warn("Geolocation requires HTTPS or localhost");
        this.showErrorMessage(
          "Location access requires a secure connection (HTTPS)"
        );
        this.userLocationDenied = true;
        return;
      }

      // Request location with options
      const options = {
        enableHighAccuracy: true,
        timeout: 10000, // 10 seconds
        maximumAge: 300000 // 5 minutes
      };

      navigator.geolocation.getCurrentPosition(
        pos => {
          const lat = pos.coords.latitude;
          const lng = pos.coords.longitude;
          this.userLocation = { lat, lng };
          this.userLocationDenied = false;
          console.log("Current location:", lat, lng);
        },
        err => {
          console.warn("Geolocation error:", err);
          this.userLocationDenied = true;
          
          let errorMessage = "Unable to get your location. ";
          switch (err.code) {
            case err.PERMISSION_DENIED:
              errorMessage +=
                "Please enable location access in your browser settings and refresh the page.";
              break;
            case err.POSITION_UNAVAILABLE:
              errorMessage += "Location information is unavailable.";
              break;
            case err.TIMEOUT:
              errorMessage += "Location request timed out.";
              break;
            default:
              errorMessage += "An unknown error occurred.";
              break;
          }
          this.showErrorMessage(errorMessage);
        },
        options
      );
    },
    checkNeedsWithinRadius() {
      // Only show alert if location is enabled and there are no needs within radius
      if (this.userLocation && !this.userLocationDenied && !this.needsWithinUserRadius) {
        this.showNoNeedsAlert = true;
        clearTimeout(this.noNeedsAlertTimeout);
        this.noNeedsAlertTimeout = setTimeout(() => {
          this.showNoNeedsAlert = false;
        }, 4000);
      } else {
        this.showNoNeedsAlert = false;
        clearTimeout(this.noNeedsAlertTimeout);
      }
    }
  },
  watch: {
    profile(newValue) {
      if (newValue.address) {
        this.getLocationFromAddress();
      }
    },
    "$route.query._id": function(newValue) {
      this.markers = this.markers.map(m => {
        if (m.template.includes(newValue)) {
          m.ifw = true;
        } else {
          m.ifw = false;
        }
        return m;
      });
    },
    "$route.query.search": function(newValue) {
      this.fetchAllNeeds({ search: newValue })
        .then(() => {
          this.loading = false;
          this.loadMarkers();
        })
        .catch(error => {
          this.loading = false;
          if (error.response == undefined) {
            console.log(error);
          } else {
            this.showErrorMessage(error.response.data.message);
          }
        });
    },
    markers: {
      handler() {
        this.checkNeedsWithinRadius();
      },
      deep: true,
      immediate: true
    },
    userLocation() {
      this.checkNeedsWithinRadius();
    },
    userLocationDenied() {
      this.checkNeedsWithinRadius();
    }
  },
  mounted() {
    this.requestAndSetUserLocation(); // Will always prompt on refresh
    if (typeof google === "undefined") {
      console.error("Google Maps API not loaded"); // Debug log
    }
    console.log("needs", this.needs);

    if (this.profile.address) {
      this.getLocationFromAddress();
    }
    this.loading = true;
    this.loadCompleted = 0;
    this.fetchAllNeeds({ search: this.$route.query.search })
      .then(() => {
        this.loading = false;
        this.loadMarkers();
      })
      .catch(error => {
        this.loading = false;
        if (error.response == undefined) {
          console.log(error);
        } else {
          this.showErrorMessage(error.response.data.message);
        }
      });
  },
  components: { AgencyNeedCard }
};
</script>
<style scoped>
@media (max-width: 600px) {
  div :deep(.v-dialog) {
    margin: 0;
    padding-top: 12px;
    padding-bottom: 12px;
  }
}
@media (min-width: 600px) {
  .v-dialog__content {
    z-index: 2000001 !important;
  }
}
.feed-body {
  width: 100%;
  min-width: 200px;
  margin-left: 0px;
  margin-right: 0px;
  display: flex;
  flex-direction: column;
  align-items: center;
}
.feed-contents {
  width: 100%;
  height: 100%;
  /* max-width: 650px; */
  align-items: center;
  display: flex;
  flex-direction: column;
  padding-bottom: 20px;
  padding-left: 20px;
  padding-right: 20px;
  padding-top: 20px;
}
@media (max-width: 600px) {
  .feed-contents {
    padding-bottom: 56px;
    padding-left: 0px;
    padding-right: 0px;
    padding-top: 0px;
    position: relative;
  }
}
.map {
  width: 100%;
  height: 100%;
}
.fade-enter-active, .fade-leave-active {
  transition: opacity 0.5s;
}
.fade-enter, .fade-leave-to {
  opacity: 0;
}
.no-needs-alert {
  /* already styled inline for clarity */
}
</style>
